import React, { useMemo, useState } from "react";
import { Pagination, A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import IconButton from "../Button/IconButton";
import ChevronLeftIcon from "assets/icons/ChevronLeftIcon";
import ChevronRightIcon from "assets/icons/ChevronRightIcon";
import useMediaQuery from "hooks/useMediaQuery";
import { lifeChoiseDetailsDesktop } from "constantVariables";
import styles from "./index.module.scss";
import { useAppSelector } from "store/hooks";
import { getSettings } from "store/slices";

const SlideBtns = ({ isRtl }) => {
  const swiper = useSwiper();

  return (
    <div className={styles["carousel-btns"]}>
      {isRtl ? (
        <>
          <IconButton
            icon={<ChevronRightIcon size={30} />}
            className={styles["carousel-btn"]}
            onClick={() => swiper.slidePrev()}
          />
          <IconButton
            icon={<ChevronLeftIcon size={30} />}
            className={styles["carousel-btn"]}
            onClick={() => swiper.slideNext()}
          />
        </>
      ) : (
        <>
          <IconButton
            icon={<ChevronLeftIcon size={30} />}
            className={styles["carousel-btn"]}
            onClick={() => swiper.slidePrev()}
          />
          <IconButton
            icon={<ChevronRightIcon size={30} />}
            className={styles["carousel-btn"]}
            onClick={() => swiper.slideNext()}
          />
        </>
      )}
    </div>
  );
};

const weekDays = [1, 2, 3, 4, 5];

const SwiperDailyScheduleButtons = () => {
  const swiper = useSwiper();

  const [isActiveIndex, setIsActiveIndex] = useState(0);
  swiper.on("slideChange", function () {
    setIsActiveIndex(swiper.realIndex);
  });
  const swiperSlideTo = (index) => {
    swiper.slideTo(index);
  };

  const { dir } = useAppSelector(getSettings);

  return (
    <div
      className={`${styles["week-days-wrapper"]} ${
        dir === "rtl" ? styles["week-days-wrapper-rtl"] : "right-0"
      } mt-16 sm:mt-6`}
    >
      {weekDays.map((day, idx) => (
        <button
          key={`--day-btn-${day}-${idx}`}
          onClick={() => swiperSlideTo(idx)}
          className={`${
            isActiveIndex === idx ? styles["active-btn"] : ""
          } !font-semibold !text-lg`}
        >
          {day}
        </button>
      ))}
    </div>
  );
};

const Carousel = ({
  renderItem,
  items,
  options,
  sliderDailyScheduleBtns,
  idx: carouselIdx,
  onSlideChange,
  className,
  renderUI,
  navigationUI,
}) => {
  const isDesktop = useMediaQuery(lifeChoiseDetailsDesktop);
  const { dir } = useAppSelector(getSettings);
  const swiper = useSwiper()

  const {
    loop,
    slideWidth,
    slidesPerView,
    centeredSlides,
    slideToClickedSlide,
    sliderBtns,
    pagination,
    spaceBetween,
  } = useMemo(() => {
    return {
      slideWidth: "auto",
      slidesPerView: "auto",
      loop: true,
      sliderBtns: false,
      ...options,
    };
  }, [options]);

  if (!items) return null;

  return (
    <>
      <Swiper
        onSlideChange={onSlideChange}
        modules={[A11y, Pagination, Navigation]}
        slidesPerView={slidesPerView}
        centeredSlides={centeredSlides}
        slideToClickedSlide={slideToClickedSlide}
        loop={loop}
        navigation={sliderBtns && isDesktop}
        allowSlideNext
        spaceBetween={spaceBetween ?? 15}
        dir={dir}
        pagination={pagination}
        key={carouselIdx}
        className={className}
        onSlide
      >
        {sliderDailyScheduleBtns && (
          <SwiperDailyScheduleButtons isRtl={dir === "rtl"} />
        )}
        {items.map((slide, idx) => (
          <SwiperSlide
            key={`--carousel-slide-${idx}`}
            style={{ width: slideWidth }}
            className={`mb-4 ${
              sliderDailyScheduleBtns ? "mt-10 lg:mt-0 !h-full" : ""
            }`}
          >
            {renderItem(slide, carouselIdx)}
          </SwiperSlide>
        ))}
        {navigationUI
          ? navigationUI
          : sliderBtns && isDesktop && <SlideBtns isRtl={dir === "rtl"} />}
      </Swiper>
      {renderUI &&
        items.map((slide, index) => (
          <React.Fragment key={index}>
            {renderUI(
              slide,
              carouselIdx
            )}
          </React.Fragment>
        ))}
    </>
  );
};

export default Carousel;
